import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { List, Search } from 'react-feather';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';

import ScheduleCrawls from '../../sites/ScheduleCrawls';
import SetCrawlNotifications from '../../sites/SetCrawlNotifications';
import SetBillingAlarms from '../../sites/SetBillingAlarms';

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'report_generated', label: 'Report Generated' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'setup_in_progress', label: 'Setup In Progress' },
  { value: 'waiting_on_client', label: 'Waiting On Client' },
  { value: 'active', label: 'Active' },
  { value: 'pause_requested', label: 'Pause Requested' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const tableStyles = {
  headRow: { style: { borderBottom: '1px solid #e9ecef', minHeight: '40px' } },
  headCells: {
    style: {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: '#6c757d',
    },
  },
  rows: { style: { minHeight: '44px', fontSize: '13px' } },
};

const SitesTable = forwardRef(
  ({ orgName, current_organization, sitegroupNames, country, siteNames }, ref) => {
    const [siteData, setSiteData] = useState([]);
    const [siteStatus, setSiteStatus] = useState('All');
    const [loading, setLoading] = useState(true);
    const [searchVal, setSearchVal] = useState('');
    const [sitegroupId, setSitegroupId] = useState('');

    const scheduleCrawlsRef = useRef();
    const setCrawlNotificationsRef = useRef();

    const [page, setPage] = useState(1);
    const countPerPage = 10;
    const [pageTotal, setPageTotal] = useState(0);

    const columns = [
      { name: 'Site Name', selector: 'site_name', sortable: true, grow: 2 },
      { name: 'Status', selector: 'site_status', sortable: true, width: '150px' },
      { name: 'Uploads (30d)', selector: 'uploads_in_last_30_days', sortable: true, right: true, width: '130px' },
      { name: 'Uploads (24h)', selector: 'uploads_in_last_24_hours', sortable: true, right: true, width: '130px' },
      { name: 'Last Run', selector: 'last_run_date', sortable: true, width: '160px' },
      { name: 'Next Run', selector: 'next_scheduled_run_date', sortable: true, width: '160px' },
      { name: 'Queue', selector: 'queue_count', sortable: true, right: true, width: '90px' },
    ];

    const handleChange = ({ selectedRows }) => {
      scheduleCrawlsRef.current?.handleRowChange(selectedRows);
      setCrawlNotificationsRef.current?.handleRowChange(selectedRows);
    };

    const buildUrl = (extra = '') => {
      const sg = sitegroupId !== '' ? `&sitegroup_id=${sitegroupId}` : '';
      return `/api/v1/organizations/${orgName}/sites?${extra}${sg}&page_type=client&controller_name=sites`;
    };

    const handleSiteStatus = (e) => {
      const v = e.target.value;
      setSiteStatus(v);
      setLoading(true);
      axios
        .get(buildUrl(`filterrific[filter_by_site_status]=${v}&page=1`))
        .then((res) => {
          setSiteData(res.data.sites);
          setPageTotal(res.data?.total_sites_count);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    };

    const handleSearch = (e) => {
      const v = e.target.value;
      setSearchVal(v);
      setLoading(true);
      axios
        .get(buildUrl(`filterrific[search_by_site_name]=${v}&page=1`))
        .then((res) => {
          setSiteData(res.data.sites);
          setPageTotal(res.data?.total_sites_count);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    };

    const handlePageChange = (pageNum) => {
      setPage(pageNum);
      setLoading(true);
      axios
        .get(
          buildUrl(
            `filterrific[filter_by_site_status]=${siteStatus}&filterrific[search_by_site_name]=${searchVal}&page=${pageNum}`
          )
        )
        .then((res) => {
          setSiteData(res.data.sites);
          setPageTotal(res.data?.total_sites_count);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    };

    useEffect(() => {
      if (!orgName) return;
      setLoading(true);
      axios
        .get(`/api/v1/organizations/${orgName}/sites?page=1&page_type=client&controller_name=sites`)
        .then((res) => {
          setSiteData(res.data.sites);
          setPageTotal(res.data?.total_sites_count);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    }, [orgName]);

    useImperativeHandle(ref, () => ({
      getSitesBySitegroupId(id) {
        setSitegroupId(id);
        if (orgName && id !== '') {
          setLoading(true);
          axios
            .get(`/api/v1/organizations/${orgName}/sites?sitegroup_id=${id}&page=1&page_type=client&controller_name=sites`)
            .then((res) => {
              setSiteData(res.data.sites);
              setPageTotal(res.data?.total_sites_count);
              setLoading(false);
            })
            .catch((err) => {
              toast.error(err?.response?.data?.message);
              setLoading(false);
            });
        }
      },
    }));

    const rowDisableCriteria = (each) => each?.site_status !== 'active';

    return (
      <Card className='shadow-sm border-0 mb-3'>
        <CardBody style={{ padding: 24 }}>
          {/* Header + bulk actions */}
          <div className='d-flex align-items-start justify-content-between flex-wrap mb-3' style={{ gap: 12 }}>
            <div className='d-flex align-items-start' style={{ gap: 14, minWidth: 0 }}>
              <div
                className='d-inline-flex align-items-center justify-content-center flex-shrink-0'
                style={{ width: 40, height: 40, borderRadius: 10, background: '#0d6efd15', color: '#0d6efd' }}
              >
                <List size={20} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h6 className='fw-semibold mb-1 text-dark' style={{ fontSize: 16 }}>
                  Sites
                </h6>
                <p className='text-muted mb-0' style={{ fontSize: 13 }}>
                  Select rows to apply bulk actions; filter by status or search by name.
                </p>
              </div>
            </div>
            <div className='d-flex align-items-center flex-wrap' style={{ gap: 6 }}>
              <span
                className='text-muted me-2'
                style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                Bulk:
              </span>
              <ScheduleCrawls orgName={orgName} sitegroupNames={sitegroupNames} siteNames={siteNames} ref={scheduleCrawlsRef} />
              <SetCrawlNotifications orgName={orgName} sitegroupNames={sitegroupNames} ref={setCrawlNotificationsRef} />
              <SetBillingAlarms orgName={orgName} country={country} current_organization={current_organization} />
            </div>
          </div>

          {/* Filters row */}
          <Row className='g-3 mb-3 pt-3' style={{ borderTop: '1px solid #f1f3f5' }}>
            <Col md={4}>
              <label
                className='text-muted d-block mb-1'
                style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                Status
              </label>
              <select
                className='form-select form-select-sm'
                value={siteStatus}
                onChange={handleSiteStatus}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Col>
            <Col md={{ size: 5, offset: 3 }}>
              <label
                className='text-muted d-block mb-1'
                style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                Search
              </label>
              <div className='position-relative'>
                <Search
                  size={14}
                  className='position-absolute'
                  style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                />
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Search by site name…'
                  value={searchVal}
                  onChange={handleSearch}
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </Col>
          </Row>

          <DataTable
            data={siteData}
            columns={columns}
            onSelectedRowsChange={handleChange}
            selectableRowDisabled={rowDisableCriteria}
            responsive
            noHeader
            selectableRows
            className='mydatatable'
            pagination
            paginationServer
            progressPending={loading}
            paginationTotalRows={pageTotal}
            paginationPerPage={countPerPage}
            paginationComponentOptions={{ noRowsPerPage: true }}
            onChangePage={handlePageChange}
            customStyles={tableStyles}
          />
        </CardBody>
      </Card>
    );
  }
);

export { SitesTable as default };
